import React, { useState } from 'react'
import _ from 'lodash'
import {
  LBSelect,
  LBSelectContent,
  LBSelectItem,
  LBSelectTrigger,
  LBSelectValue,
  LBBadge,
  LBAlertDialog,
  LBAlertDialogAction,
  LBAlertDialogCancel,
  LBAlertDialogContent,
  LBAlertDialogDescription,
  LBAlertDialogFooter,
  LBAlertDialogHeader,
  LBAlertDialogTitle
} from '@linearb/buzz'
import { setEnvironment, ENVIRONMENTS } from '../constants/environments'
import { CURRENT_ENVIRONMENT, IS_PROD_EU } from '../constants/apis'

const EnvironmentSelector: React.FC = () => {
  const currentEnv = CURRENT_ENVIRONMENT
  const [pendingEnvId, setPendingEnvId] = useState<string | null>(null)

  if (!currentEnv) {
    return null
  }

  const handleEnvironmentChange = (envId: string) => {
    if (envId !== currentEnv.id) {
      setPendingEnvId(envId)
    }
  }

  const handleConfirmSwitch = () => {
    if (pendingEnvId) {
      setEnvironment(pendingEnvId)
      setPendingEnvId(null)
    }
  }

  const handleCancelSwitch = () => {
    setPendingEnvId(null)
  }

  return (
    <>
      <div className='flex items-center gap-2'>
        <LBSelect value={currentEnv.id} onValueChange={handleEnvironmentChange}>
          <LBSelectTrigger
            className={`w-[140px] h-8 ${IS_PROD_EU ? 'border-blue-600 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300' : ''}`}
          >
            <LBSelectValue>
              <span className='flex items-center gap-2'>
                <span aria-hidden>{currentEnv.flag}</span>
                <span className='font-mono'>{currentEnv.id}</span>
              </span>
            </LBSelectValue>
          </LBSelectTrigger>
          <LBSelectContent className='w-[340px]'>
            {_.filter(ENVIRONMENTS, { tier: currentEnv.tier }).map(env => (
              <LBSelectItem
                key={env.id}
                value={env.id}
                className='h-auto py-2.5 [&>span:last-child]:flex-1'
                disabled={!env.enabled}
              >
                <div className='flex items-center gap-3 w-full min-w-full pr-6'>
                  <span aria-hidden className='text-base'>
                    {env.flag}
                  </span>
                  <LBBadge variant='secondary' className='font-mono text-xs'>
                    {env.id}
                  </LBBadge>
                  <div className='flex flex-col flex-1 items-end text-xs'>
                    <span className='text-foreground'>{env.city}</span>
                    <span className='text-muted-foreground font-mono'>
                      {env.region}
                    </span>
                  </div>
                </div>
              </LBSelectItem>
            ))}
          </LBSelectContent>
        </LBSelect>
      </div>

      <LBAlertDialog open={!!pendingEnvId} onOpenChange={handleCancelSwitch}>
        <LBAlertDialogContent>
          <LBAlertDialogHeader>
            <LBAlertDialogTitle>Switch Environment?</LBAlertDialogTitle>
            <LBAlertDialogDescription>
              You are about to switch from{' '}
              <strong>
                {currentEnv.flag} {currentEnv.id}
              </strong>{' '}
              to{' '}
              <strong>
                {ENVIRONMENTS[pendingEnvId]?.flag} {pendingEnvId}
              </strong>
              .
            </LBAlertDialogDescription>
          </LBAlertDialogHeader>
          <LBAlertDialogFooter>
            <LBAlertDialogCancel onClick={handleCancelSwitch}>
              Cancel
            </LBAlertDialogCancel>
            <LBAlertDialogAction onClick={handleConfirmSwitch}>
              Switch Environment
            </LBAlertDialogAction>
          </LBAlertDialogFooter>
        </LBAlertDialogContent>
      </LBAlertDialog>
    </>
  )
}

export default EnvironmentSelector
